import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { formatDate, hasTextContent, stripHtml, fromDateKey, toDateKey, getContrastColor } from '../utils';
import Modal from './Modal';
import Icon from './Icon';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const PREVIEW_LIMIT = 300;

// ─── HELPERS ─────────────────────────────────────────────────────────────────

const safeSlug = (title) =>
  (title || 'timeline').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'timeline';

const dateKeyToDays = (key) => {
  if (!key) return 0;
  if (key.length === 12) {
    const { year, month, day, era } = fromDateKey(key);
    const astro = era === 'BC' ? 1 - year : year;
    return astro * 365.25 + (month || 1) * 30.44 + (day || 1);
  }
  return new Date(key + 'T00:00:00').getTime() / 86400000;
};

const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const getGranularity = (spanYears) => {
  if (spanYears < 10)   return 'year';
  if (spanYears < 100)  return 'decade';
  if (spanYears < 1000) return 'century';
  return 'millennium';
};

const getSectionKey = (ev, granularity) => {
  if (!ev.startDate || ev.startDate.length < 12) return null;
  const { year, era } = fromDateKey(ev.startDate);
  const astro = era === 'BC' ? 1 - year : year;
  switch (granularity) {
    case 'year':       return astro;
    case 'decade':     return Math.floor(astro / 10) * 10;
    case 'century':    return Math.floor(astro / 100) * 100;
    case 'millennium': return Math.floor(astro / 1000) * 1000;
    default:           return astro;
  }
};

const formatSectionLabel = (key, granularity) => {
  const absKey = Math.abs(key);
  const isBC   = key < 0;
  switch (granularity) {
    case 'year':
      return key >= 1 ? String(key) : `${1 - key} BC`;
    case 'decade':
      return isBC ? `${absKey}s BC` : `${key}s`;
    case 'century': {
      const n = Math.floor(absKey / 100) + 1;
      return isBC ? `${ordinal(n)} Century BC` : `${ordinal(n)} Century`;
    }
    case 'millennium': {
      const n = Math.floor(absKey / 1000) + 1;
      return isBC ? `${ordinal(n)} Millennium BC` : `${ordinal(n)} Millennium`;
    }
    default:
      return String(key);
  }
};

// ─── PHOTO CARD ──────────────────────────────────────────────────────────────

const PhotoCard = ({ src, alt, colors, loading = 'lazy' }) => {
  const [loaded,    setLoaded]    = useState(false);
  const [errored,   setErrored]   = useState(false);
  const [objectPos, setObjectPos] = useState('center');

  const handleLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setObjectPos(naturalHeight > naturalWidth ? 'center top' : 'center');
    setLoaded(true);
  };

  return (
    <div className="vt-photo-wrap" style={{ background: `${colors.tertiary}80` }}>
      {!loaded && !errored && (
        <div className="vt-photo-skeleton" aria-hidden="true" />
      )}
      {errored ? (
        <div className="vt-photo-fallback" aria-label="Image unavailable">
          <Icon name="image" size={22} />
          <span>Image unavailable</span>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className="vt-photo"
          style={{ objectPosition: objectPos, opacity: loaded ? 1 : 0 }}
          loading={loading}
          onLoad={handleLoad}
          onError={() => setErrored(true)}
        />
      )}
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const VerticalTimeline = ({
  events      = [],
  colors,
  onEventClick,
  manualOrder = false,
  timeline    = null,
  isEmbed     = false,
}) => {

  // ── State ────────────────────────────────────────────────────────────────────
  const [expandedIds,    setExpandedIds]    = useState(new Set());
  const [filterFading,   setFilterFading]   = useState(false);
  const [activeChapters, setActiveChapters] = useState(null); // null = All
  const [sectionsOn,     setSectionsOn]     = useState(false);
  const [alternatingOn,  setAlternatingOn]  = useState(false);
  const [jumpDay,        setJumpDay]        = useState('');
  const [jumpMonth,      setJumpMonth]      = useState('');
  const [jumpYear,       setJumpYear]       = useState('');
  const [jumpEra,        setJumpEra]        = useState('AD');
  const [jumpMsg,        setJumpMsg]        = useState('');
  const [jumpOk,         setJumpOk]         = useState(false);
  const [highlightId,    setHighlightId]    = useState(null);
  const [scrollPct,      setScrollPct]      = useState(0);
  const [pastFirst,      setPastFirst]      = useState(false);
  const [atBottom,       setAtBottom]       = useState(false);
  const [tickPositions,  setTickPositions]  = useState([]);
  const [isExporting,    setIsExporting]    = useState(false);
  const [exportMsg,      setExportMsg]      = useState('');
  const [embedOpen,      setEmbedOpen]      = useState(false);
  const [embedCopied,    setEmbedCopied]    = useState(false);
  const [embedWidth,     setEmbedWidth]     = useState('800');
  const [embedHeight,    setEmbedHeight]    = useState('700');
  const [embedResp,      setEmbedResp]      = useState(true);

  // ── Refs ─────────────────────────────────────────────────────────────────────
  const containerRef   = useRef(null);
  const cardRefs       = useRef({});
  const sectionRefs    = useRef({});
  const highlightTimer = useRef(null);

  // ── Sorted events ─────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    const arr = [...events];
    if (manualOrder) {
      arr.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    } else {
      arr.sort((a, b) => {
        const d = (a.startDate || "").localeCompare(b.startDate || "");
        return d !== 0 ? d : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      });
    }
    return arr;
  }, [events, manualOrder]);

  // ── Chapter data ──────────────────────────────────────────────────────────────
  const chapters         = timeline?.chapters || [];
  const usedChapterIds   = useMemo(() => new Set(sorted.map(ev => ev.chapterId).filter(Boolean)), [sorted]);
  const visibleChapters  = chapters.filter(c => usedChapterIds.has(c.id));
  const hasAnyChapter    = usedChapterIds.size > 0;
  const hasUncategorized = sorted.some(ev => !ev.chapterId);
  const showFilterBar    = hasAnyChapter;

  // ── Filtered events ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    if (!activeChapters || activeChapters.size === 0) return sorted;
    return sorted.filter(ev => {
      if (activeChapters.has('__uncategorized__') && !ev.chapterId) return true;
      return ev.chapterId && activeChapters.has(ev.chapterId);
    });
  }, [sorted, activeChapters]);

  // ── Long descriptions ─────────────────────────────────────────────────────────
  const hasLongDescs = useMemo(
    () => sorted.some(ev => stripHtml(ev.description || '').length > PREVIEW_LIMIT),
    [sorted],
  );

  // ── Timeline span & granularity (section headers) ─────────────────────────────
  const { spanYears, granularity } = useMemo(() => {
    if (sorted.length < 2) return { spanYears: 0, granularity: 'year' };
    const firstDays = dateKeyToDays(sorted[0].startDate);
    const lastDays  = dateKeyToDays(sorted[sorted.length - 1].startDate);
    const span      = Math.abs(lastDays - firstDays) / 365.25;
    return { spanYears: span, granularity: getGranularity(span) };
  }, [sorted]);

  // ── Embed constants ───────────────────────────────────────────────────────────
  const isPublic  = timeline?.privacy === 'public';
  const embedUrl  = timeline?.id
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/embed/${timeline.id}`
    : '';
  const safeTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const wAttr     = embedResp ? '100%' : embedWidth;
  const hAttr     = embedResp ? '100%' : embedHeight;
  const embedCode = `<iframe\n  src="${embedUrl}"\n  width="${wAttr}"\n  height="${hAttr}"\n  style="border:none;border-radius:12px;"\n  title="${safeTitle}"\n  allowfullscreen\n></iframe>`;

  const copyEmbed = useCallback(() => {
    navigator.clipboard?.writeText(embedCode).catch(() => {});
    setEmbedCopied(true);
    setTimeout(() => setEmbedCopied(false), 2000);
  }, [embedCode]);

  // ── Scroll progress listener ──────────────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const scrollY  = window.scrollY;
      const docH     = document.documentElement.scrollHeight;
      const viewH    = window.innerHeight;
      const maxScroll = docH - viewH;
      const pct = maxScroll > 0 ? scrollY / maxScroll : 0;
      setScrollPct(Math.min(1, Math.max(0, pct)));
      setPastFirst(scrollY > 200);
      setAtBottom(pct > 0.97);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // ── Chapter tick marks on progress bar ───────────────────────────────────────
  useEffect(() => {
    if (!sectionsOn) { setTickPositions([]); return; }
    const compute = () => {
      const docH     = document.documentElement.scrollHeight;
      const viewH    = window.innerHeight;
      const maxScroll = docH - viewH;
      if (maxScroll <= 0) return;
      const ticks = Object.entries(sectionRefs.current)
        .filter(([, el]) => el)
        .map(([key, el]) => {
          const elTop = el.getBoundingClientRect().top + window.scrollY;
          const pct   = Math.max(0, Math.min(1, (elTop - viewH * 0.3) / maxScroll));
          const label = el.querySelector('.vt-section-text')?.textContent || String(key);
          return { key, label, pct };
        });
      setTickPositions(ticks);
    };
    // Wait a frame for layout
    const id = requestAnimationFrame(compute);
    return () => cancelAnimationFrame(id);
  }, [sectionsOn, filtered]);

  // ── Deep link scroll (URL hash #event-{id}) ──────────────────────────────────
  useEffect(() => {
    const hash  = window.location.hash;
    const match = hash.match(/^#event-([a-z0-9_-]+)$/i);
    if (!match) return;
    const targetId = match[1];
    const timer = setTimeout(() => {
      const el = cardRefs.current[targetId];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        triggerHighlight(targetId);
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const triggerHighlight = (id) => {
    clearTimeout(highlightTimer.current);
    setHighlightId(id);
    highlightTimer.current = setTimeout(() => setHighlightId(null), 2500);
  };

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedIds(new Set(sorted.map(ev => ev.id)));
  }, [sorted]);

  const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

  const applyChapterFilter = useCallback((newFilter) => {
    setFilterFading(true);
    setTimeout(() => {
      setActiveChapters(newFilter);
      setFilterFading(false);
    }, 180);
  }, []);

  const toggleChapter = useCallback((id) => {
    applyChapterFilter(prev => {
      if (!prev) return new Set([id]);
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next.size === 0 ? null : next;
      }
      next.add(id);
      return next;
    });
  }, [applyChapterFilter]);

  // ── Jump to date ──────────────────────────────────────────────────────────────
  const handleJump = () => {
    const year = parseInt(jumpYear, 10);
    if (!year) { setJumpMsg('Please enter a year.'); setJumpOk(false); return; }
    const month     = parseInt(jumpMonth, 10) || 1;
    const day       = parseInt(jumpDay,   10) || 1;
    const targetKey  = toDateKey(year, month, day, jumpEra);
    const targetDays = dateKeyToDays(targetKey);

    if (sorted.length === 0) { setJumpMsg('No events on this timeline.'); setJumpOk(false); return; }

    const firstDays = dateKeyToDays(sorted[0].startDate);
    const lastDays  = dateKeyToDays(sorted[sorted.length - 1].startDate);
    if (targetDays < firstDays - 365 || targetDays > lastDays + 365) {
      setJumpMsg(
        `No events near this date. Timeline spans ${formatDate(sorted[0].startDate, true)} to ${formatDate(sorted[sorted.length - 1].startDate, true)}.`,
      );
      setJumpOk(false);
      return;
    }

    let nearest = sorted[0];
    let minDiff  = Math.abs(dateKeyToDays(nearest.startDate) - targetDays);
    for (const ev of sorted) {
      const diff = Math.abs(dateKeyToDays(ev.startDate) - targetDays);
      if (diff < minDiff) { minDiff = diff; nearest = ev; }
    }

    setJumpMsg(`↳ ${nearest.title} — ${formatDate(nearest.startDate, true)}`);
    setJumpOk(true);
    const el = cardRefs.current[nearest.id];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      triggerHighlight(nearest.id);
    }
  };

  const clearJump = () => {
    setJumpDay(''); setJumpMonth(''); setJumpYear(''); setJumpEra('AD'); setJumpMsg(''); setJumpOk(false);
  };

  // ── PNG export ────────────────────────────────────────────────────────────────
  const handleExportPNG = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Preparing export…');

    const prevExpanded = new Set(expandedIds);
    const prevChapters = activeChapters;

    setExpandedIds(new Set(sorted.map(ev => ev.id)));
    setActiveChapters(null);
    await new Promise(r => setTimeout(r, 250));

    try {
      const el = containerRef.current;
      await document.fonts.ready;

      const origOverflow = el.style.overflow;
      const origHeight   = el.style.height;
      el.style.overflow  = 'visible';
      el.style.height    = `${el.scrollHeight}px`;

      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale:           2,
          useCORS:         true,
          allowTaint:      true,
          backgroundColor: null,
          width:           el.offsetWidth,
          height:          el.scrollHeight,
          scrollX:         0,
          scrollY:         -window.scrollY,
        });
      } finally {
        el.style.overflow = origOverflow;
        el.style.height   = origHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `${safeSlug(timeline?.title)}_${date}.png`;
      link.href     = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert(`PNG export failed: ${err.message}`);
    } finally {
      setExpandedIds(prevExpanded);
      setActiveChapters(prevChapters);
      setIsExporting(false);
      setExportMsg('');
    }
  }, [expandedIds, activeChapters, sorted, timeline]);

  // ── PDF export ────────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    setExportMsg('Generating PDF…');

    const prevExpanded = new Set(expandedIds);
    const prevChapters = activeChapters;

    setExpandedIds(new Set(sorted.map(ev => ev.id)));
    setActiveChapters(null);
    await new Promise(r => setTimeout(r, 250));

    try {
      // Snapshot card geometry BEFORE capture for page-break avoidance.
      // Use getBoundingClientRect differentials so coordinates are always
      // relative to the container, regardless of each card's offsetParent chain.
      const containerTop = el.getBoundingClientRect().top;
      const cardRects = sorted
        .map(ev => {
          const c = cardRefs.current[ev.id];
          if (!c) return null;
          const r = c.getBoundingClientRect();
          return { top: r.top - containerTop, bottom: r.bottom - containerTop };
        })
        .filter(Boolean);

      const el = containerRef.current;
      await document.fonts.ready;

      const origOverflow = el.style.overflow;
      const origHeight   = el.style.height;
      el.style.overflow  = 'visible';
      el.style.height    = `${el.scrollHeight}px`;

      let canvas;
      try {
        canvas = await html2canvas(el, {
          scale:           2,
          useCORS:         true,
          allowTaint:      true,
          backgroundColor: null,
          width:           el.offsetWidth,
          height:          el.scrollHeight,
          scrollX:         0,
          scrollY:         -window.scrollY,
        });
      } finally {
        el.style.overflow = origOverflow;
        el.style.height   = origHeight;
      }

      // ── Page constants ─────────────────────────────────────────────────────
      const pageW    = 595.28;   // A4 portrait pt
      const pageH    = 841.89;
      const MARGIN   = 36;
      const FOOTER_H = 20;
      const LINE_H   = 13;
      const contentW = pageW - MARGIN * 2;

      const titleText  = timeline?.title  || 'Timeline';
      const authorText = timeline?.author || '';
      const descRaw    = timeline?.description ? stripHtml(timeline.description).trim() : '';
      const today      = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

      // Pre-compute header height
      pdf.setFontSize(10);
      const descLines = descRaw ? pdf.splitTextToSize(descRaw, contentW) : [];
      let HEADER = MARGIN + 24;
      if (descLines.length) HEADER += descLines.length * LINE_H + 6;
      if (authorText)       HEADER += LINE_H + 4;
      HEADER += 8;

      // Canvas metrics
      const scale2x    = 2;
      const canvasNatW = canvas.width  / scale2x;
      const canvasNatH = canvas.height / scale2x;
      const scaleRatio = contentW / canvasNatW;           // pt per CSS px
      const pageHcss   = (pageH - FOOTER_H - MARGIN) / scaleRatio; // CSS px per page

      // Build page break points, avoiding card splits within 80 CSS px of a break
      setExportMsg('Calculating layout…');
      const pageBreaks = [];
      let curY = 0;
      while (curY < canvasNatH) {
        const firstPage    = pageBreaks.length === 0;
        const availH       = firstPage ? (pageH - HEADER - FOOTER_H) / scaleRatio : pageHcss;
        const naturalBreak = curY + availH;

        if (naturalBreak >= canvasNatH) {
          pageBreaks.push(canvasNatH);
          break;
        }

        // If the natural break falls inside a card, push the break up to
        // that card's top so the card starts on the next page intact.
        const splitCard = cardRects.find(({ top, bottom }) =>
          top > curY && top < naturalBreak && bottom > naturalBreak,
        );
        const breakPt = splitCard ? splitCard.top : naturalBreak;
        pageBreaks.push(Math.min(breakPt, canvasNatH));
        curY = breakPt;
      }

      const numTimelinePages = pageBreaks.length;

      // Pre-compute All Events data
      setExportMsg('Building events list…');
      const eventsData = sorted.map(ev => {
        const desc = stripHtml(ev.description || '').trim();
        pdf.setFontSize(10);
        const wrapped = desc ? pdf.splitTextToSize(desc, contentW) : [];
        return {
          title:   ev.title,
          date:    ev.endDate && ev.endDate !== ev.startDate ? `${formatDate(ev.startDate, ev.startEra !== 'NONE')} → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}` : formatDate(ev.startDate, ev.startEra !== 'NONE'),
          wrapped,
        };
      });

      // Simulate All Events page count
      let simY = MARGIN + LINE_H * 2.5;
      let numEventPages = 1;
      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;
        if (simY + blockH > pageH - FOOTER_H - LINE_H) { simY = MARGIN + LINE_H; numEventPages++; }
        simY += blockH;
      }

      const totalPages = numTimelinePages + numEventPages;

      const drawFooter = (pageNum) => {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(160, 160, 160);
        pdf.text('Chronos', MARGIN, pageH - MARGIN / 2);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pageW - MARGIN, pageH - MARGIN / 2, { align: 'right' });
      };

      // Write timeline canvas pages
      setExportMsg('Rendering timeline…');
      let curSrcY = 0;
      for (let pageIdx = 0; pageIdx < pageBreaks.length; pageIdx++) {
        if (pageIdx > 0) pdf.addPage();

        if (pageIdx === 0) {
          let hy = MARGIN + 20;
          pdf.setFont('times', 'bold');
          pdf.setFontSize(20);
          pdf.setTextColor(30, 30, 30);
          pdf.text(titleText, MARGIN, hy);
          hy += 26;

          if (descLines.length) {
            pdf.setFont('times', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(80, 80, 80);
            pdf.text(descLines, MARGIN, hy);
            hy += descLines.length * LINE_H + 6;
          }

          if (authorText) {
            pdf.setFont('times', 'italic');
            pdf.setFontSize(11);
            pdf.setTextColor(110, 110, 110);
            pdf.text(authorText, MARGIN, hy);
          }

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(9);
          pdf.setTextColor(140, 140, 140);
          pdf.text(today, pageW - MARGIN, MARGIN + 20, { align: 'right' });
        }

        const breakEndY  = pageBreaks[pageIdx];
        const sliceH_css = breakEndY - curSrcY;
        const slice      = document.createElement('canvas');
        slice.width      = canvas.width;
        slice.height     = sliceH_css * scale2x;
        slice.getContext('2d').drawImage(canvas, 0, -curSrcY * scale2x);

        const dstH = sliceH_css * scaleRatio;
        const topY = pageIdx === 0 ? HEADER : MARGIN;
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', MARGIN, topY, contentW, dstH);

        drawFooter(pageIdx + 1);
        curSrcY = breakEndY;
      }

      // Write All Events pages
      setExportMsg('Writing events…');
      pdf.addPage();
      let evPage = numTimelinePages + 1;
      let y = MARGIN + LINE_H;

      pdf.setFont('times', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(30, 30, 30);
      pdf.text(`All Events  (${eventsData.length})`, MARGIN, y);
      y += LINE_H * 2;

      pdf.setDrawColor(180, 180, 180);
      pdf.setLineWidth(0.5);
      pdf.line(MARGIN, y - LINE_H * 0.5, pageW - MARGIN, y - LINE_H * 0.5);

      for (const ev of eventsData) {
        const blockH = LINE_H * 1.6 + LINE_H + ev.wrapped.length * LINE_H + 10;
        if (y + blockH > pageH - FOOTER_H - LINE_H) {
          drawFooter(evPage);
          pdf.addPage();
          evPage++;
          y = MARGIN + LINE_H;
        }
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(12);
        pdf.setTextColor(30, 30, 30);
        pdf.text(ev.title, MARGIN, y);
        y += LINE_H * 1.6;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(110, 110, 110);
        pdf.text(ev.date, MARGIN, y);
        y += LINE_H;

        if (ev.wrapped.length > 0) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(55, 55, 55);
          pdf.text(ev.wrapped, MARGIN, y);
          y += ev.wrapped.length * LINE_H;
        }

        y += 4;
        pdf.setDrawColor(210, 210, 210);
        pdf.setLineWidth(0.3);
        pdf.line(MARGIN, y, pageW - MARGIN, y);
        y += 6;
      }

      drawFooter(evPage);

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`${safeSlug(titleText)}_${date}.pdf`);
    } catch (err) {
      alert(`PDF export failed: ${err.message}`);
    } finally {
      setExpandedIds(prevExpanded);
      setActiveChapters(prevChapters);
      setIsExporting(false);
      setExportMsg('');
    }
  }, [expandedIds, activeChapters, sorted, timeline]);

  // ── Progress bar click → scroll ───────────────────────────────────────────────
  const handleProgressClick = useCallback((e) => {
    const rect  = e.currentTarget.getBoundingClientRect();
    const pct   = (e.clientY - rect.top) / rect.height;
    const docH  = document.documentElement.scrollHeight;
    const viewH = window.innerHeight;
    window.scrollTo({ top: pct * (docH - viewH), behavior: 'smooth' });
  }, []);

  // ── Float nav helpers ─────────────────────────────────────────────────────────
  const scrollToFirst = useCallback(() => {
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const scrollToLast = useCallback(() => {
    const lastId = filtered[filtered.length - 1]?.id;
    const el     = lastId ? cardRefs.current[lastId] : null;
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [filtered]);

  // ── Empty state ───────────────────────────────────────────────────────────────
  if (sorted.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: 'var(--text3)' }}>
        No events yet. Add your first event.
      </div>
    );
  }

  // ── Build display items (events + optional section headers) ───────────────────
  const displayItems = [];
  if (sectionsOn && sorted.length >= 2) {
    let lastKey = null;
    for (const ev of filtered) {
      const key = getSectionKey(ev, granularity);
      if (key !== null && key !== lastKey) {
        const count = filtered.filter(e => getSectionKey(e, granularity) === key).length;
        displayItems.push({ type: 'section', key, label: formatSectionLabel(key, granularity), count });
        lastKey = key;
      }
      displayItems.push({ type: 'event', ev });
    }
  } else {
    filtered.forEach(ev => displayItems.push({ type: 'event', ev }));
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className="vt-root"
      style={{ '--vt-primary': colors.primary }}
      data-exporting={isExporting || undefined}
    >

      {/* ── Export overlay ──────────────────────────────────────────────────────── */}
      {isExporting && (
        <div className="vt-export-overlay vt-no-export" aria-live="polite">
          <div className="vt-export-card">
            <div className="ht-export-msg">{exportMsg}</div>
          </div>
        </div>
      )}

      {/* ── Toolbar ─────────────────────────────────────────────────────────────── */}
      {!isEmbed && (
        <div className="vt-toolbar vt-no-export" role="toolbar" aria-label="Timeline controls">
          <button
            className={`vt-btn${sectionsOn ? ' vt-btn-active' : ''}`}
            onClick={() => setSectionsOn(v => !v)}
            title="Toggle auto section headers"
          >
            <Icon name="layers" size={13} />
            Sections
          </button>

          <button
            className={`vt-btn${alternatingOn ? ' vt-btn-active' : ''}`}
            onClick={() => setAlternatingOn(v => !v)}
            title="Toggle alternating card colors"
          >
            Alternating
          </button>

          <div className="vt-toolbar-sep" aria-hidden="true" />

          <button
            className="vt-btn vt-btn-export"
            onClick={handleExportPNG}
            title="Export as PNG"
            disabled={isExporting}
          >
            <Icon name="image" size={13} />
            PNG
          </button>

          <button
            className="vt-btn vt-btn-export"
            onClick={handleExportPDF}
            title="Export as PDF"
            disabled={isExporting}
          >
            <Icon name="fileText" size={13} />
            PDF
          </button>

          {timeline && (
            <button
              className="vt-btn vt-btn-export"
              onClick={() => setEmbedOpen(true)}
              title={isPublic ? 'Get embed code' : 'Make the timeline public to embed it'}
              disabled={!isPublic}
            >
              <Icon name="code" size={13} />
              Embed
            </button>
          )}
        </div>
      )}

      {/* ── Controls row: count badge + expand/collapse ──────────────────────────── */}
      {!isEmbed && (
        <div className={`vt-controls-row${showFilterBar ? ' vt-controls-row--with-filter' : ''} vt-no-export`}>
          <span
            className="vt-count-badge"
            style={{ background: colors.secondary }}
          >
            {activeChapters && activeChapters.size > 0
              ? `${filtered.length} of ${sorted.length} Events`
              : `${sorted.length} Events`}
          </span>

          {hasLongDescs && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="vt-btn" onClick={expandAll}>Expand All</button>
              <button className="vt-btn" onClick={collapseAll}>Collapse All</button>
            </div>
          )}
        </div>
      )}

      {/* ── Chapter filter pills ─────────────────────────────────────────────────── */}
      {showFilterBar && (
        <div className="vt-filter-bar vt-no-export" role="group" aria-label="Filter by chapter">
          <button
            className={`vt-pill${!activeChapters ? ' vt-pill-active' : ''}`}
            style={!activeChapters ? { borderColor: colors.primary, background: `${colors.primary}22`, color: colors.primary } : {}}
            onClick={() => applyChapterFilter(null)}
          >
            All ({sorted.length})
          </button>

          {visibleChapters.map(ch => {
            const count  = sorted.filter(ev => ev.chapterId === ch.id).length;
            const active = !!activeChapters?.has(ch.id);
            return (
              <button
                key={ch.id}
                className={`vt-pill${active ? ' vt-pill-active' : ''}`}
                style={active ? { borderColor: colors.primary, background: `${colors.primary}22`, color: colors.primary } : {}}
                onClick={() => toggleChapter(ch.id)}
              >
                {ch.title} ({count})
              </button>
            );
          })}

          {hasUncategorized && (
            <button
              className={`vt-pill${activeChapters?.has('__uncategorized__') ? ' vt-pill-active' : ''}`}
              style={activeChapters?.has('__uncategorized__') ? { borderColor: colors.primary, background: `${colors.primary}22`, color: colors.primary } : {}}
              onClick={() => toggleChapter('__uncategorized__')}
            >
              Uncategorized ({sorted.filter(ev => !ev.chapterId).length})
            </button>
          )}
        </div>
      )}

      {/* ── Jump to date ─────────────────────────────────────────────────────────── */}
      {!isEmbed && (
        <div className="vt-jump-bar vt-no-export">
          <span className="vt-jump-label">Jump to date</span>
          <input
            className="vt-jump-input"
            type="number"
            placeholder="Day"
            min={1} max={31}
            value={jumpDay}
            onChange={e => setJumpDay(e.target.value)}
            aria-label="Day (optional)"
          />
          <input
            className="vt-jump-input"
            type="number"
            placeholder="Month"
            min={1} max={12}
            value={jumpMonth}
            onChange={e => setJumpMonth(e.target.value)}
            aria-label="Month (optional)"
          />
          <input
            className="vt-jump-input vt-jump-year"
            type="number"
            placeholder="Year *"
            value={jumpYear}
            onChange={e => setJumpYear(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleJump()}
            aria-label="Year (required)"
          />
          <select
            className="vt-jump-era"
            value={jumpEra}
            onChange={e => setJumpEra(e.target.value)}
            aria-label="Era"
          >
            <option value="AD">—</option>
            <option value="BC">B.C.</option>
          </select>
          <button className="vt-btn" onClick={handleJump}>Go</button>
          {(jumpDay || jumpMonth || jumpYear) && (
            <button className="vt-btn" onClick={clearJump} aria-label="Clear">
              <Icon name="close" size={12} />
            </button>
          )}
          {jumpMsg && <span className={`vt-jump-msg${jumpOk ? ' vt-jump-msg--ok' : ''}`}>{jumpMsg}</span>}
        </div>
      )}

      {/* ── Empty filter state ───────────────────────────────────────────────────── */}
      {filtered.length === 0 && sorted.length > 0 && (
        <div className="vt-empty-filter vt-no-export">
          <p style={{ color: 'var(--text2)', marginBottom: 10 }}>No events in this chapter.</p>
          <button className="vt-btn" onClick={() => applyChapterFilter(null)}>Clear Filter</button>
        </div>
      )}

      {/* ── Event list ──────────────────────────────────────────────────────────── */}
      <ol
        aria-label="Timeline events"
        style={{
          maxWidth: 700,
          margin: '0 auto',
          padding: '20px 16px',
          listStyle: 'none',
          opacity: filterFading ? 0 : 1,
          transition: 'opacity 180ms ease',
        }}
      >
        {displayItems.map((item, idx) => {

          // ── Section header ──
          if (item.type === 'section') {
            return (
              <li key={`section-${item.key}`} aria-hidden="true" style={{ display: 'block', margin: '8px 0 4px' }}>
                <div
                  ref={el => { sectionRefs.current[item.key] = el; }}
                  className="vt-section-header"
                  style={{ background: colors.primary }}
                >
                  <span className="vt-section-rule" style={{ background: `${getContrastColor(colors.primary)}40` }} />
                  <span className="vt-section-label">
                    <span className="vt-section-text" style={{ color: getContrastColor(colors.primary) }}>
                      {item.label}
                    </span>
                    <span
                      className="vt-section-count"
                      style={{ background: `${getContrastColor(colors.primary)}25`, color: getContrastColor(colors.primary) }}
                    >
                      {item.count}
                    </span>
                  </span>
                  <span className="vt-section-rule" style={{ background: `${getContrastColor(colors.primary)}40` }} />
                </div>
              </li>
            );
          }

          // ── Event card ──
          const { ev } = item;
          const plainDesc   = stripHtml(ev.description || '');
          const isLong      = plainDesc.length > PREVIEW_LIMIT;
          const isExpanded  = expandedIds.has(ev.id);
          const preview     = isLong && !isExpanded ? plainDesc.slice(0, PREVIEW_LIMIT) + '…' : null;
          const isHighlight = highlightId === ev.id;

          // Alternating: count only event items up to and including this one
          const cardIdx = displayItems.slice(0, idx + 1).filter(it => it.type === 'event').length - 1;
          const cardBg  = alternatingOn
            ? cardIdx % 2 === 0 ? `${colors.tertiary}cc` : `${colors.tertiary}aa`
            : `${colors.tertiary}cc`;

          // Whether there's a next spine connector (look ahead for next event or end)
          const hasNextConnector = idx < displayItems.length - 1;

          return (
            <li
              key={ev.id}
              style={{ display: 'flex', gap: 0, position: 'relative', marginBottom: 0 }}
            >
              {/* Left — date column (hidden on phones) */}
              <div
                aria-hidden="true"
                className="vt-date-col"
                style={{ width: 100, flexShrink: 0, paddingTop: 12, textAlign: 'right', paddingRight: 20 }}
              >
                <span style={{ fontSize: 11, color: colors.primary, fontWeight: 600, lineHeight: 1.3, display: 'block' }}>
                  {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                </span>
                {ev.endDate !== ev.startDate && (
                  <span style={{ fontSize: 10, color: colors.secondary, display: 'block' }}>
                    → {formatDate(ev.endDate, ev.endEra !== 'NONE')}
                  </span>
                )}
              </div>

              {/* Centre — spine & dot */}
              <div
                aria-hidden="true"
                style={{ width: 20, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <div style={{
                  width: 12, height: 12, borderRadius: '50%',
                  background: colors.primary, border: `2px solid ${colors.secondary}`,
                  flexShrink: 0, marginTop: 16, zIndex: 1,
                }} />
                {hasNextConnector && (
                  <div style={{
                    width: 2, flex: 1,
                    background: `linear-gradient(${colors.primary}80, ${colors.secondary}40)`,
                    minHeight: 40,
                  }} />
                )}
              </div>

              {/* Right — event card */}
              <div className="vt-card-col" style={{ flex: 1, paddingLeft: 16, paddingBottom: 32 }}>
                <div
                  ref={el => { cardRefs.current[ev.id] = el; }}
                  data-event-id={ev.id}
                  className={`vt-card${isHighlight ? ' vt-card-highlight' : ''}`}
                  style={{
                    background:   cardBg,
                    border:       `1px solid ${colors.primary}40`,
                    borderRadius: 'var(--radius-lg)',
                    padding:      '14px 18px',
                    cursor:       'pointer',
                    transition:   'border-color 0.2s, background 0.2s, transform 0.2s',
                  }}
                  onClick={() => onEventClick(ev)}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = colors.secondary;
                    e.currentTarget.style.background  = `${colors.secondary}22`;
                    e.currentTarget.style.transform   = 'translateX(4px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = `${colors.primary}40`;
                    e.currentTarget.style.background  = cardBg;
                    e.currentTarget.style.transform   = 'none';
                  }}
                >
                  {/* Inline date on phones */}
                  <p
                    className="vt-date-inline"
                    aria-hidden="true"
                    style={{ display: 'none', fontSize: 10, color: colors.secondary, fontWeight: 600, marginBottom: 4 }}
                  >
                    {formatDate(ev.startDate, ev.startEra !== 'NONE')}
                    {ev.endDate !== ev.startDate && ` → ${formatDate(ev.endDate, ev.endEra !== 'NONE')}`}
                  </p>

                  {/* Title */}
                  <h4 style={{ fontSize: 15, fontWeight: 600, color: colors.primary, marginBottom: 6 }}>
                    {ev.title}
                  </h4>

                  {/* Photo */}
                  {ev.photo && (
                    <PhotoCard
                      src={ev.photo}
                      alt={`Photo for ${ev.title}`}
                      colors={colors}
                      loading={cardIdx === 0 ? 'eager' : 'lazy'}
                    />
                  )}

                  {/* Description */}
                  {hasTextContent(ev.description) && (
                    <>
                      <div
                        className="vt-desc-wrap"
                        style={{ maxHeight: isExpanded ? '4000px' : preview ? '6em' : '4000px' }}
                      >
                        {isExpanded ? (
                          <div
                            className="vt-desc-rich"
                            dangerouslySetInnerHTML={{ __html: ev.description }}
                            onClick={e => e.stopPropagation()}
                          />
                        ) : (
                          <p className="vt-desc-preview">
                            {preview ?? plainDesc}
                          </p>
                        )}
                      </div>

                      {isLong && (
                        <button
                          className="vt-read-more-btn vt-no-export"
                          style={{ color: colors.secondary }}
                          onClick={e => { e.stopPropagation(); toggleExpand(ev.id); }}
                        >
                          {isExpanded ? 'Read Less ↑' : 'Read More ↓'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>

      {/* ── Scroll progress bar ──────────────────────────────────────────────────── */}
      {!isEmbed && (
        <div
          className="vt-progress-track vt-no-export"
          onClick={handleProgressClick}
          role="scrollbar"
          aria-orientation="vertical"
          aria-valuenow={Math.round(scrollPct * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Timeline scroll progress"
          style={{ background: `${colors.tertiary}80`, borderColor: `${colors.primary}30` }}
        >
          <div
            className="vt-progress-fill"
            style={{ height: `${scrollPct * 100}%`, background: colors.primary }}
          />
          {tickPositions.map(tick => (
            <div
              key={tick.key}
              className="vt-progress-tick"
              style={{ top: `${tick.pct * 100}%`, background: colors.secondary }}
              title={tick.label}
            />
          ))}
        </div>
      )}

      {/* ── First / Last floating nav ─────────────────────────────────────────────── */}
      {!isEmbed && (pastFirst || !atBottom) && filtered.length > 0 && (
        <div className="vt-float-nav vt-no-export">
          {pastFirst && (
            <button
              className="vt-float-btn"
              onClick={scrollToFirst}
              aria-label="Jump to first event"
              style={{ background: colors.secondary, color: getContrastColor(colors.secondary) }}
            >
              ↑ First
            </button>
          )}
          {!atBottom && (
            <button
              className="vt-float-btn"
              onClick={scrollToLast}
              aria-label="Jump to last event"
              style={{ background: colors.secondary, color: getContrastColor(colors.secondary) }}
            >
              ↓ Last
            </button>
          )}
        </div>
      )}

      {/* ── Embed modal ──────────────────────────────────────────────────────────── */}
      {timeline && (
        <Modal
          open={embedOpen}
          onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }}
          title="Embed Timeline"
          width={580}
        >
          <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
            Paste this snippet into any webpage to embed{' '}
            <strong style={{ color: 'var(--text)' }}>{timeline?.title}</strong> as an interactive iframe.
          </p>

          {/* Code block */}
          <div style={{
            background: 'var(--bg3)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12,
          }}>
            <div style={{ padding: '6px 14px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>HTML</span>
            </div>
            <pre style={{
              padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75,
              color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto',
              whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
              {embedCode}
            </pre>
          </div>

          {/* Size controls */}
          <div className="ht-embed-controls">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
              <input type="checkbox" checked={embedResp} onChange={e => setEmbedResp(e.target.checked)} style={{ width: 'auto' }} />
              Responsive (100% width)
            </label>
            {!embedResp && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ marginBottom: 4 }}>Width</label>
                  <input value={embedWidth} onChange={e => setEmbedWidth(e.target.value)} placeholder="e.g. 800 or 100%" style={{ fontSize: 13 }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ marginBottom: 4 }}>Height (px)</label>
                  <input value={embedHeight} onChange={e => setEmbedHeight(e.target.value)} placeholder="e.g. 700" style={{ fontSize: 13 }} />
                </div>
              </div>
            )}
            {/* Height advisory for vertical timelines */}
            {!embedResp && parseInt(embedHeight, 10) > 0 && parseInt(embedHeight, 10) < 400 && (
              <p style={{ fontSize: 12, color: 'var(--danger)', margin: 0 }}>
                Vertical timelines display best at 600px height or taller.
              </p>
            )}
            {!embedResp && parseInt(embedHeight, 10) >= 400 && parseInt(embedHeight, 10) < 600 && (
              <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
                Recommended minimum height: 600px for vertical timelines.
              </p>
            )}
            {/* Preset sizes — portrait-friendly dimensions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {[['Small', '600', '500'], ['Medium', '800', '700'], ['Large', '1000', '900']].map(([name, w, h]) => (
                <button
                  key={name}
                  className="vt-btn"
                  onClick={() => { setEmbedWidth(w); setEmbedHeight(h); setEmbedResp(false); }}
                  style={{ fontSize: 12 }}
                >
                  {name} ({w}×{h})
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 20 }}>
            <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>
              Close
            </button>
            <button
              className="btn-primary"
              onClick={copyEmbed}
              style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 148 }}
            >
              <Icon name={embedCopied ? 'check' : 'copy'} size={14} />
              {embedCopied ? 'Copied!' : 'Copy Snippet'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default VerticalTimeline;
